import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Building2, Users, FileText, HardDrive, Zap, Crown, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing`);
      if (res.ok) {
        const data = await res.json();
        setPricingData(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleSelectPlan = (planCode) => {
    if (user && token) {
      navigate('/workspace/status');
    } else {
      navigate('/register');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <Badge variant="secondary" className="mb-4">Prezzi</Badge>
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Scegli il piano giusto per te
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
            Gestisci i tuoi condomini in modo professionale con Artego. 
            Inizia con una prova gratuita di 10 giorni.
          </p>
          {pricingData?.vat_note && (
            <p className="mt-2 text-sm text-orange-600 font-medium">
              {pricingData.vat_note}
            </p>
          )}
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mt-10">
          <div className="bg-gray-100 p-1 rounded-lg inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mensile
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annuale
              <Badge variant="default" className="ml-2 bg-green-600">-17%</Badge>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Trial Card */}
          {pricingData?.trial && (
            <Card className="relative border-2 border-gray-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  <CardTitle>{pricingData.trial.name}</CardTitle>
                </div>
                <CardDescription>{pricingData.trial.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <span className="text-4xl font-bold">Gratis</span>
                  <span className="text-gray-500 ml-2">per {pricingData.trial.duration_days} giorni</span>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span>{pricingData.trial.limits.max_condominiums} condomini</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span>{pricingData.trial.limits.max_units} unità</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span>{pricingData.trial.limits.max_documents} documenti</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <HardDrive className="h-4 w-4 text-gray-400" />
                    <span>{Math.round(pricingData.trial.limits.max_storage_mb / 1024)} GB storage</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/register')}
                >
                  Inizia la prova gratuita
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Paid Plans */}
          {pricingData?.plans?.map((plan) => (
            <Card 
              key={plan.code}
              className={`relative border-2 ${
                plan.highlighted 
                  ? 'border-blue-600 shadow-lg' 
                  : 'border-gray-200'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white">Consigliato</Badge>
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-center gap-2">
                  {plan.code === 'PRO' ? (
                    <Crown className="h-5 w-5 text-purple-500" />
                  ) : (
                    <Building2 className="h-5 w-5 text-blue-500" />
                  )}
                  <CardTitle>{plan.name}</CardTitle>
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="mb-6">
                  <span className="text-4xl font-bold">
                    {formatPrice(billingCycle === 'yearly' 
                      ? Math.round(plan.pricing.yearly_price / 12) 
                      : plan.pricing.monthly_price
                    )}
                  </span>
                  <span className="text-gray-500 ml-1">+ IVA/mese</span>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 mt-1">
                      {formatPrice(plan.pricing.yearly_price)} + IVA/anno (risparmi {formatPrice(plan.pricing.monthly_price * 12 - plan.pricing.yearly_price)})
                    </p>
                  )}
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span><strong>{plan.limits.max_condominiums}</strong> condomini</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span><strong>{plan.limits.max_units.toLocaleString()}</strong> unità</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span><strong>{plan.limits.max_users}</strong> collaboratori</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <HardDrive className="h-4 w-4 text-gray-400" />
                    <span><strong>{Math.round(plan.limits.max_storage_mb / 1024)}</strong> GB storage</span>
                  </li>
                </ul>

                {/* Upgrade Option */}
                {pricingData?.upgrades?.[plan.code] && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-semibold text-blue-800 mb-1">
                      Pacchetto Upgrade: +{formatPrice(pricingData.upgrades[plan.code].price_monthly)} + IVA/mese
                    </p>
                    <ul className="text-xs text-blue-700 space-y-0.5">
                      {pricingData.upgrades[plan.code].includes.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-medium mb-3">Funzionalità incluse:</p>
                  <ul className="space-y-2">
                    {plan.features.email_invites && (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Inviti email
                      </li>
                    )}
                    {plan.features.whatsapp_invites && (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Inviti WhatsApp
                      </li>
                    )}
                    {plan.features.backup_scheduled && (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Backup automatici
                      </li>
                    )}
                    {plan.features.bank_reconciliation && (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Riconciliazione bancaria
                      </li>
                    )}
                    {plan.features.api_access ? (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Accesso API
                      </li>
                    ) : (
                      <li className="flex items-center gap-2 text-sm text-gray-400">
                        <X className="h-4 w-4" /> Accesso API
                      </li>
                    )}
                    {plan.features.priority_support ? (
                      <li className="flex items-center gap-2 text-sm text-green-700">
                        <Check className="h-4 w-4" /> Supporto prioritario
                      </li>
                    ) : (
                      <li className="flex items-center gap-2 text-sm text-gray-400">
                        <X className="h-4 w-4" /> Supporto prioritario
                      </li>
                    )}
                  </ul>
                </div>
              </CardContent>
              
              <CardFooter>
                <Button 
                  className={`w-full ${plan.highlighted ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  onClick={() => handleSelectPlan(plan.code)}
                >
                  Richiedi attivazione
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Features Comparison */}
        {pricingData?.features_comparison && (
          <div className="mt-20">
            <h2 className="text-2xl font-bold text-center mb-8">Confronto funzionalità</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Funzionalità</th>
                    <th className="text-center py-3 px-4 font-medium">Basic</th>
                    <th className="text-center py-3 px-4 font-medium">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingData.features_comparison.map((feature, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-3 px-4">{feature.name}</td>
                      <td className="text-center py-3 px-4">
                        {feature.basic === '✓' ? (
                          <Check className="h-5 w-5 text-green-600 mx-auto" />
                        ) : feature.basic === '—' ? (
                          <X className="h-5 w-5 text-gray-300 mx-auto" />
                        ) : (
                          feature.basic
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {feature.pro === '✓' ? (
                          <Check className="h-5 w-5 text-green-600 mx-auto" />
                        ) : feature.pro === '—' ? (
                          <X className="h-5 w-5 text-gray-300 mx-auto" />
                        ) : (
                          feature.pro
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-20 text-center bg-gray-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4">Hai bisogno di un piano personalizzato?</h2>
          <p className="text-gray-600 mb-6">
            Contattaci per discutere le tue esigenze specifiche e ricevere un&apos;offerta su misura.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" asChild>
              <a href={`mailto:${pricingData?.contact?.email}`}>
                {pricingData?.contact?.email}
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={`tel:${pricingData?.contact?.phone}`}>
                {pricingData?.contact?.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-stone-50 py-6 px-6 md:px-8 lg:px-12 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src="/assets/artego-symbol.png"
                alt="Artego SRLS"
                className="h-10 w-auto"
              />
              <div className="text-sm text-stone-600">
                <p className="font-semibold text-stone-800">Artego SRLS</p>
                <p>Via M.se di Villabianca, 114 - 90143 Palermo</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 text-sm text-stone-600">
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Contatti</span>
                <a href="tel:+393520498295" className="hover:text-slate-900">+39 3520498295</a>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Email</span>
                <a href="mailto:info@artegosrls.it" className="hover:text-slate-900">info@artegosrls.it</a>
                <a href="mailto:artegosrls@pec.it" className="hover:text-slate-900">artegosrls@pec.it</a>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">P.IVA</span>
                <span>07221580827</span>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-stone-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-stone-500">
            <div className="flex items-center gap-2">
              <p>© 2026 Artego SRLS. Tutti i diritti riservati.</p>
              <span className="hidden md:inline">•</span>
              <a href="https://www.artegosrls.it" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 font-medium">
                www.artegosrls.it
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors" aria-label="Facebook">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors" aria-label="Instagram">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://wa.me/393520498295" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-green-600 transition-colors" aria-label="WhatsApp">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a href="https://www.youtube.com/@artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-600 transition-colors" aria-label="YouTube">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
